import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { similar_Properties } from "../../../../redux/reducers/Properties/PropertiesSlice";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import StayCardSkeleton from "shared/Skelton/CardSkeltion";
import SimilarCard from "components/StayCard/SimilarCard";

interface SimilarPropertyProps {
  propertyData: PropertyToEdit | null;
}

const SimilarProperty: React.FC<SimilarPropertyProps> = ({ propertyData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [similarProperties, setSimilarProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const property_type_id = propertyData?.property_type?.id;
  const property_category_id = propertyData?.property_category?.id;
  const property_id = propertyData?.id;

  const fetchSimilarProperties = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await dispatch(
        similar_Properties({
          property_type_id: property_type_id ?? 0,
          property_category_id: property_category_id ?? 0,
          property_id: property_id ?? 0,
        })
      ).unwrap();
      setSimilarProperties(response?.properties || []);
    } catch (err: any) {
      console.error("Error fetching similar properties:", err);
      setError(err.message || "Failed to fetch similar properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (property_type_id && property_category_id && property_id) {
      fetchSimilarProperties();
    }
  }, [property_type_id, property_category_id, property_id]);

  return (
    <div className="listingSection__wrap w-full space-y-6">

      {/* Section header */}
      <div className="flex items-end justify-between">
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <span className="block w-8 h-0.5 bg-[#F56948] rounded-full" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#F56948]">
              Explore
            </span>
          </div>
          <h2 className="text-2xl font-extrabold text-[#1A1A2E] dark:text-white tracking-tight">
            Similar Properties
          </h2>
          <p className="text-sm text-neutral-400 dark:text-neutral-500">
            Discover more properties like this one
          </p>
        </div>

        {/* Count pill — only when loaded */}
        {!loading && !error && similarProperties.length > 0 && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F56948]/10 border border-[#F56948]/20 self-start">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F56948]" />
            <span className="text-xs font-semibold text-[#F56948]">
              {similarProperties.length} {similarProperties.length === 1 ? "property" : "properties"}
            </span>
          </div>
        )}
      </div>

      <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

      {/* Loading */}
      {loading && (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2">
          {[...Array(3)].map((_, i) => (
            <StayCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-14 gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <svg className="w-7 h-7 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-[#1A1A2E] dark:text-white">Failed to load</p>
            <p className="text-xs text-neutral-400 mt-1">{error}</p>
          </div>
          <button
            onClick={fetchSimilarProperties}
            className="px-5 py-2.5 rounded-xl text-xs font-bold bg-[#F56948] text-white hover:bg-[#e0522f] transition-colors shadow-sm"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && similarProperties.length === 0 && (
        <div className="flex flex-col items-center justify-center py-14 gap-3 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#F56948]/10 flex items-center justify-center">
            <svg className="w-7 h-7 text-[#F56948]/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-[#1A1A2E] dark:text-white">No similar properties</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-xs leading-relaxed">
              We couldn't find any similar properties at the moment. Check back later.
            </p>
          </div>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && similarProperties.length > 0 && (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2">
          {similarProperties.map((property, index) => (
            <SimilarCard
              key={property.id || index}
              similarProperties={property}
            />
          ))}
        </div>
      )}

    </div>
  );
};

export default SimilarProperty;